import { api } from 'services/api';

import { extractDuplicatedBudgetId } from './conversionUtils';
import {
  BudgetConversionInconsistencyApiItem,
  BudgetConversionInconsistencyListResponse,
  BudgetConversionInconsistencyQueryParams,
} from './types';

const INCONSISTENCY_FILTER_TYPE = 'input_original,composition_custom';
const INCONSISTENCY_CHILDREN_FILTER_TYPE = 'composition_original';
const INCONSISTENCY_FILTER_OR_LEVEL = 4;
const DEFAULT_IDENTIFICATION_POLL_INTERVAL_MS = 1500;
const DEFAULT_IDENTIFICATION_POLL_MAX_ATTEMPTS = 40;

export const INVALID_DUPLICATE_RESPONSE_MESSAGE =
  'Invalid duplicate budget response';
export const BUDGET_IDENTIFICATION_TIMEOUT_MESSAGE =
  'Budget identification was not ready in time';

type FetchBudgetConversionInconsistenciesParams = {
  page: number;
  per_page: number;
  sort?: string;
};

export const fetchBudgetConversionInconsistencies = async (
  budgetId: number,
  params: FetchBudgetConversionInconsistenciesParams,
): Promise<BudgetConversionInconsistencyListResponse> => {
  const requestParams: BudgetConversionInconsistencyQueryParams = {
    page: params.page,
    per_page: params.per_page,
    ...(params.sort ? { sort: params.sort } : {}),
    'filter[level_or_type][type]': INCONSISTENCY_FILTER_TYPE,
    'filter[level_or_type][level]': INCONSISTENCY_FILTER_OR_LEVEL,
  };

  const response = await api.get(`/budget/${budgetId}/component`, {
    params: requestParams,
  });

  return response.data;
};

export const fetchBudgetComponentsByParentStageId = async (
  budgetId: number,
  parentStageId: number,
): Promise<BudgetConversionInconsistencyApiItem[]> => {
  const { data: response } = await api.get(`/budget/${budgetId}/component`, {
    params: {
      'filter[level_or_type][type]': INCONSISTENCY_CHILDREN_FILTER_TYPE,
      'filter[parent_stage_id]': parentStageId,
      sort: 'position',
    },
  });

  if (!Array.isArray(response?.data)) {
    return [];
  }

  return response.data;
};

export const exportBudgetConversionInconsistencies = async (
  budgetId: number,
): Promise<void> => {
  await api.get(`/budget/${budgetId}/export`, {
    params: {
      report_type: 'conversion_inconsistencies',
    },
  });
};

export const duplicateBudgetAsSienge = async (
  budgetId: number,
): Promise<number> => {
  const response = await api.post(
    `/budget/${budgetId}/duplicate`,
    {},
    {
      params: {
        'filter[convert_common_to_sienge]': true,
      },
    },
  );

  const duplicatedBudgetId = extractDuplicatedBudgetId(response.data);

  if (!duplicatedBudgetId) {
    throw new Error(INVALID_DUPLICATE_RESPONSE_MESSAGE);
  }

  return duplicatedBudgetId;
};

type WaitForBudgetIdentificationOptions = {
  pollIntervalMs?: number;
  maxAttempts?: number;
};

const sleep = async (ms: number): Promise<void> => {
  await new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

const hasIdentification = (value: unknown): boolean => {
  return value != null;
};

export const waitForBudgetIdentification = async (
  budgetId: number,
  options?: WaitForBudgetIdentificationOptions,
): Promise<void> => {
  const pollIntervalMs =
    options?.pollIntervalMs ?? DEFAULT_IDENTIFICATION_POLL_INTERVAL_MS;
  const maxAttempts =
    options?.maxAttempts ?? DEFAULT_IDENTIFICATION_POLL_MAX_ATTEMPTS;

  const pollAttempt = async (attempt: number): Promise<void> => {
    const response = await api.get(`/budget/${budgetId}`);
    const identification = response?.data?.data?.identification;

    if (hasIdentification(identification)) {
      return;
    }

    const isLastAttempt = attempt >= maxAttempts - 1;
    if (isLastAttempt) {
      throw new Error(BUDGET_IDENTIFICATION_TIMEOUT_MESSAGE);
    }

    await sleep(pollIntervalMs);
    await pollAttempt(attempt + 1);
  };

  await pollAttempt(0);
};
